import { useLayoutEffect, useState } from 'react';

export const MOBILE_WINDOW_WIDTH = 768;

export const useWindowSize = (): { width: number; height: number; isMobile: boolean } => {
  const [size, setSize] = useState<[number, number]>([window.innerWidth, window.innerHeight]);

  useLayoutEffect(() => {
    const updateSize = () => {
      setSize([window.innerWidth, window.innerHeight]);
    };

    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  return {
    width: size[0],
    height: size[1],
    isMobile: size[0] <= MOBILE_WINDOW_WIDTH,
  };
};

export const printDocument = (
  printContentId: string,
  printFrameId: string,
  onClose: () => void,
  margin?: number,
  size = 'auto'
): void => {
  const content = document.getElementById(printContentId) as HTMLElement;
  const printScope = (document.getElementById(printFrameId) as HTMLIFrameElement).contentWindow as WindowProxy;
  const style = `<style>@page { size: ${size}; margin: ${Number(margin) >= 0 ? margin : 30}px; }</style>`;

  printScope.document.open();
  printScope.document.write(content.innerHTML + style);
  printScope.document.close();
  setTimeout(() => {
    printScope.focus();
    printScope.print();
    if (onClose) {
      onClose();
    }
  }, 1000);
};

interface PdfLoadingTheme {
  backgroundColor: string;
  paperColor: string;
  primaryColor: string;
  textColor: string;
  secondaryTextColor: string;
  dividerColor: string;
  fontFamily?: string;
  borderRadius: string | number;
  shadow: string;
}

export const renderPdfLoadingState = (pdfWindow: Window, loadingTheme: PdfLoadingTheme): void => {
  const { document } = pdfWindow;

  document.title = 'Preparing PDF';

  const style = document.createElement('style');

  style.textContent = `
    * {
      box-sizing: border-box;
    }

    html,
    body {
      width: 100%;
      height: 100%;
      margin: 0;
    }

    body {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 24px;
      font-family: ${loadingTheme.fontFamily};
      background-color: ${loadingTheme.backgroundColor};
      color: ${loadingTheme.textColor};
    }

    .loading-card {
      display: flex;
      flex-direction: column;
      align-items: center;
      min-width: 280px;
      padding: 32px;
      border: 1px solid ${loadingTheme.dividerColor};
      border-radius: ${loadingTheme.borderRadius}px;
      background-color: ${loadingTheme.paperColor};
      box-shadow: ${loadingTheme.shadow};
    }

    .spinner {
      width: 40px;
      height: 40px;
      margin-bottom: 20px;
      border: 4px solid ${loadingTheme.dividerColor};
      border-top-color: ${loadingTheme.primaryColor};
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    }

    .title {
      margin: 0;
      font-size: 1.25rem;
      font-weight: 500;
      line-height: 1.6;
    }

    .message {
      margin: 4px 0 0;
      color: ${loadingTheme.secondaryTextColor};
      font-size: 0.875rem;
      line-height: 1.43;
    }

    @keyframes spin {
      to {
        transform: rotate(360deg);
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .spinner {
        animation-duration: 1.5s;
      }
    }
  `;

  const card = document.createElement('div');
  card.className = 'loading-card';

  const spinner = document.createElement('div');
  spinner.className = 'spinner';
  spinner.setAttribute('role', 'progressbar');
  spinner.setAttribute('aria-label', 'Preparing PDF');

  const title = document.createElement('p');
  title.className = 'title';
  title.textContent = 'Preparing PDF';

  const message = document.createElement('p');
  message.className = 'message';
  message.textContent = 'Your document will appear shortly.';

  card.append(spinner, title, message);
  document.head.appendChild(style);
  document.body.replaceChildren(card);
};
